import { differenceInCalendarDays, format } from 'date-fns'
import { generatePath } from 'react-router-dom'
import { routes } from '../../router/routes'

/**
 * Feature switch for the What's new page and every entry point to it (menus,
 * footer, home rail). It ships dark: until the Ark side and the
 * `announcementsList` / `announcementRetrieve` events exist the page would be
 * presenting sample notes as if they were real, so it is on for local
 * development and opt-in per environment (`REACT_APP_WHATS_NEW_ENABLED=true`),
 * the same build-time switch Gleap uses. `REACT_APP_WHATS_NEW_ENABLED=false`
 * hides it locally too.
 */
export const WHATS_NEW_ENABLED =
  process.env.REACT_APP_WHATS_NEW_ENABLED === 'true' ||
  (process.env.NODE_ENV === 'development' && process.env.REACT_APP_WHATS_NEW_ENABLED !== 'false')

/**
 * Flip to `true` the moment the two events exist on the bus. Everything either
 * side of this flag is already the real path — the slice, the selectors, the
 * event-bus definitions and the components stay exactly as they are; only
 * `mock-announcements.js` and the two mock branches in `useAnnouncements` go
 * away (README.md has the checklist). Gates telemetry too.
 */
export const WHATS_NEW_API_ENABLED = false

/** `{ from, size }` paging, same envelope as the notification centre. */
export const WHATS_NEW_PAGE_SIZE = 10

/**
 * A note is "new" while it is at most this many days old. Zero persistence
 * (PENG-3662, "Better" tier): the chip, the home rail and the nav badge all
 * derive from `publishedAt` alone, so nothing has to be stored per user.
 */
export const WHATS_NEW_FRESH_DAYS = 14

/** The home rail shows at most this many fresh notes. */
export const WHATS_NEW_HOME_RAIL_MAX = 6

/**
 * The `type` field the story asks for ("clear distinction between platform
 * releases, feature announcements, and other update types"). Not in Icaro's
 * draft model yet — raised as a gap; the UI falls back to `announcement`.
 */
export const UPDATE_TYPE = {
  FEATURE: 'feature',
  IMPROVEMENT: 'improvement',
  FIX: 'fix',
  ANNOUNCEMENT: 'announcement'
}

export const UPDATE_TYPE_LABEL = {
  [UPDATE_TYPE.FEATURE]: 'New feature',
  [UPDATE_TYPE.IMPROVEMENT]: 'Improvement',
  [UPDATE_TYPE.FIX]: 'Fix',
  [UPDATE_TYPE.ANNOUNCEMENT]: 'Announcement'
}

export const UPDATE_TYPE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: UPDATE_TYPE.FEATURE, label: 'New features' },
  { key: UPDATE_TYPE.IMPROVEMENT, label: 'Improvements' },
  { key: UPDATE_TYPE.FIX, label: 'Fixes' },
  { key: UPDATE_TYPE.ANNOUNCEMENT, label: 'Announcements' }
]

/** Audience axis from the discovery; resolved server-side, the mock needs it. */
export const ANNOUNCEMENT_AUDIENCE = {
  ALL: 'all',
  ENTERPRISE: 'enterprise',
  CONSUMER: 'consumer'
}

/**
 * Where a visit came from — the entry-point debate gets settled by data.
 * LIST and DETAIL are moves between the What's new pages, not entries.
 */
export const WHATS_NEW_ENTRY_SOURCE = {
  MENU: 'menu',
  DRAWER: 'drawer',
  FOOTER: 'footer',
  HOME: 'home',
  LIST: 'list',
  DETAIL: 'detail',
  DIRECT: 'direct'
}

export const isInternalSource = source =>
  source === WHATS_NEW_ENTRY_SOURCE.LIST || source === WHATS_NEW_ENTRY_SOURCE.DETAIL

/** Copy lives here because the page, the rail and the states share it. */
export const WHATS_NEW_COPY = {
  title: "What's new",
  intro: 'The latest features, improvements and fixes in Mindtools, newest first.',
  metaTitle: "Mindtools | What's new",
  feedback: 'Provide feedback',
  feedbackShort: 'Feedback',
  lastUpdate: 'Last update',
  allUpdates: 'All updates',
  loadMore: 'Load more updates',
  readMore: 'Read the update',
  moreUpdates: 'More updates',
  useful: 'Was this update useful?',
  usefulThanks: 'Thanks for letting us know.',
  filterByType: 'Filter by type',
  clearFilters: 'Clear all filters',
  clear: 'Clear',
  filterByFeature: 'Filter by feature',
  filterByFeatureNote: 'Only features included in your plan are listed.',
  homeRailTitle: "What's new in Mindtools",
  homeRailCta: 'See all updates',
  contentRailBody: 'This page is about the product. New articles, videos and Skill Bites picked for you are on your Home page.',
  contentRailCta: 'Go to Home',
  empty: {
    title: "You're all caught up.",
    body: 'New features and improvements will appear here as we release them.'
  },
  error: {
    title: "Couldn't load what's new.",
    body: 'Check your connection and try again.',
    retry: 'Try again'
  },
  itemError: {
    title: "Couldn't load this update.",
    body: 'Check your connection and try again.',
    retry: 'Try again'
  },
  notFound: {
    title: "We couldn't find that update.",
    body: 'It may have been removed, or the link is out of date.'
  },
  endOfList: {
    title: "That's everything.",
    body: "You've reached the first update we published."
  }
}

const toDate = value => {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return isNaN(date.getTime()) ? null : date
}

/** True while `publishedAt` is within WHATS_NEW_FRESH_DAYS of `now`. */
export const isFresh = (publishedAt, now = new Date()) => {
  const date = toDate(publishedAt)
  if (!date) return false
  const age = differenceInCalendarDays(now, date)
  return age >= 0 && age <= WHATS_NEW_FRESH_DAYS
}

/**
 * Absolute, never relative: a changelog is a record, and "3w" ages badly.
 * "12 Sep 2026".
 */
export const formatPublishedAt = publishedAt => {
  const date = toDate(publishedAt)
  return date ? format(date, 'd MMM yyyy') : ''
}

/**
 * Groups a reverse-chron list by calendar day of `publishedAt`, keeping the
 * order it came in. Date-of-release headers as in Harvest — not month
 * grouping, which the discovery dropped. Notes without a date fall into one
 * trailing group with `date: null`.
 */
export const groupByPublishedDate = (items = []) => {
  const groups = []
  items.forEach(item => {
    const date = toDate(item?.publishedAt)
    const key = date ? format(date, 'yyyy-MM-dd') : ''
    const last = groups[groups.length - 1]
    if (last && last.key === key) {
      last.items.push(item)
      return
    }
    groups.push({ key, date: date ? item.publishedAt : null, items: [item] })
  })
  return groups
}

export const getUpdateTypeKey = type =>
  UPDATE_TYPE_LABEL[type] ? type : UPDATE_TYPE.ANNOUNCEMENT

export const getUpdateTypeLabel = type => UPDATE_TYPE_LABEL[getUpdateTypeKey(type)]

export const whatsNewPath = source =>
  source ? `${routes['whats-new'].path}?from=${source}` : routes['whats-new'].path

export const announcementPath = (id, source) => {
  const path = generatePath(routes['whats-new-item'].path, { announcementId: id })
  return source ? `${path}?from=${source}` : path
}

/**
 * Only the notes a user is entitled to see: a `featureId` the tenant lacks
 * hides the note outright (never tease), and the audience must match. The
 * server applies exactly this predicate (PENG-3662); the client copy exists
 * for the mock and for tests. Tenant.features values are either a boolean
 * (mindsetAi, msb) or an `{ enabled }` object (skillBite, playlist), so both
 * shapes are read.
 */
export const isVisibleTo = (announcement, { features = {}, audience } = {}) => {
  if (!announcement) return false
  const flag = features[announcement.featureId]
  const enabled = flag !== null && typeof flag === 'object' ? flag.enabled !== false : flag !== false
  const featureOk = !announcement.featureId || enabled
  const audienceOk =
    !announcement.audience ||
    announcement.audience === ANNOUNCEMENT_AUDIENCE.ALL ||
    announcement.audience === audience
  return featureOk && audienceOk
}
